import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Mail, Package, Clock, Truck } from 'lucide-react';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { getRMA, getPublicSettings } from '@/lib/api';

const RMAConfirmation = () => {
  const { rmaId } = useParams();
  const [rma, setRma] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [publicSettings, setPublicSettings] = useState({});

  useEffect(() => {
    const loadRMA = async () => {
      try {
        const [data, settings] = await Promise.all([
          getRMA(rmaId),
          getPublicSettings().catch(() => ({}))
        ]);
        setRma(data);
        setPublicSettings(settings);
      } catch (error) {
        console.error('Error loading RMA:', error);
      }
      setIsLoading(false);
    };
    
    loadRMA();
    
    // Clear session storage
    sessionStorage.removeItem('currentOrder');
    sessionStorage.removeItem('selectedItems');
    sessionStorage.removeItem('requestType');
  }, [rmaId]);

  const getReasonLabel = (reason) => {
    const labels = {
      too_small: 'Troppo stretto/a',
      too_large: 'Troppo largo/a',
      different_fit: 'Vestibilità diversa',
      different_color: 'Colore diverso',
      defect: 'Difetto prodotto',
      changed_mind: 'Ho cambiato idea',
      other: 'Altro motivo'
    };
    return labels[reason] || reason;
  };

  if (isLoading) {
    return (
      <div className="page-container bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <p>Caricamento...</p>
        </main>
      </div>
    );
  }

  const isDefectCase = rma?.items?.some(item => item.q3_return_reason === 'defect');

  return (
    <div className="page-container bg-white">
      <Header />
      
      <main className="flex-1 py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="mb-8"
          >
            <div className="w-20 h-20 mx-auto bg-emerald-500 text-white flex items-center justify-center">
              <CheckCircle className="w-10 h-10" />
            </div>
          </motion.div>
          
          <motion.h1 
            className="font-heading text-4xl md:text-5xl font-normal tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Reso Confermato!
          </motion.h1>
          
          <motion.p 
            className="text-lg text-muted-foreground font-light mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            La tua richiesta di reso è stata ricevuta con successo
          </motion.p>

          {/* Refund Info */}
          {rma?.refund_amount !== null && (
            <motion.div 
              className="bg-emerald-50 border border-emerald-200 p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-emerald-600 mb-2">
                Rimborso Stimato
              </p>
              <p className="text-4xl font-bold text-emerald-700">
                €{parseFloat(rma.refund_amount || 0).toFixed(2)}
              </p>
              {rma.return_cost > 0 && (
                <p className="text-sm text-emerald-600 mt-2">
                  (al netto del costo reso di €{rma.return_cost.toFixed(2)})
                </p>
              )}
              {isDefectCase && (
                <p className="text-sm text-emerald-600 mt-2 font-medium">
                  ✓ Reso gratuito per difetto
                </p>
              )}
            </motion.div>
          )}

          {/* Request Details */}
          <motion.div 
            className="luxury-card p-8 mb-8 text-left"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <div className="space-y-6">
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Ordine
                </p>
                <p className="font-medium">{rma?.order_number}</p>
              </div>

              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Articoli in Reso
                </p>
                <div className="space-y-2">
                  {rma?.items?.map((item, i) => (
                    <div key={i} className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.variant_title}</p>
                        <p className="text-xs text-muted-foreground">
                          Motivo: {getReasonLabel(item.q3_return_reason)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                  Modalità Restituzione
                </p>
                {(() => {
                  const labels = publicSettings.delivery_method_labels || {};
                  const method = rma?.return_method || publicSettings.shipping_method_rma || 'pickup';
                  const methodLabel = labels[`${method}_label`] || (method === 'pickup' ? 'Ritiro a domicilio' : method === 'label' ? 'Spedizione autonoma' : 'Spedizione guidata');
                  const methodDesc = labels[`${method}_description`] || '';
                  const returnAddress = publicSettings.return_shipping_address || {};
                  return (
                    <div>
                      <div className="bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
                        <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-blue-800">{methodLabel}</p>
                          {methodDesc && <p className="text-sm text-blue-700">{methodDesc}</p>}
                        </div>
                      </div>
                      {(method === 'label' || method === 'free') && returnAddress.street && (
                        <div className="mt-3 bg-gray-50 border border-gray-200 p-4">
                          <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2">
                            Indirizzo per la Spedizione
                          </p>
                          <p className="font-medium text-sm">{returnAddress.name || 'DEA FEA'}</p>
                          <p className="text-sm text-muted-foreground">{returnAddress.street}</p>
                          <p className="text-sm text-muted-foreground">{returnAddress.zip} {returnAddress.city} ({returnAddress.province})</p>
                          <p className="text-sm text-muted-foreground">{returnAddress.country || 'Italia'}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div 
            className="grid md:grid-cols-3 gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {(() => {
              const method = rma?.return_method || publicSettings.shipping_method_rma || 'pickup';
              if (method === 'pickup') {
                return (
                  <>
                    <div className="luxury-card p-5 text-left">
                      <Mail className="w-5 h-5 mb-3 text-primary" />
                      <h3 className="font-medium text-sm mb-1">Conferma Email</h3>
                      <p className="text-xs text-muted-foreground">
                        Riceverai un'email con i dettagli del reso
                      </p>
                    </div>
                    <div className="luxury-card p-5 text-left">
                      <Package className="w-5 h-5 mb-3 text-primary" />
                      <h3 className="font-medium text-sm mb-1">Prepara il Pacco</h3>
                      <p className="text-xs text-muted-foreground">
                        Imballa il prodotto in modo sicuro
                      </p>
                    </div>
                    <div className="luxury-card p-5 text-left">
                      <Clock className="w-5 h-5 mb-3 text-primary" />
                      <h3 className="font-medium text-sm mb-1">Attendi il Corriere</h3>
                      <p className="text-xs text-muted-foreground">
                        Ti contatteremo per il ritiro
                      </p>
                    </div>
                  </>
                );
              }
              // label or free: customer ships the package themselves
              return (
                <>
                  <div className="luxury-card p-5 text-left">
                    <Mail className="w-5 h-5 mb-3 text-primary" />
                    <h3 className="font-medium text-sm mb-1">Conferma Email</h3>
                    <p className="text-xs text-muted-foreground">
                      Riceverai un'email con i dettagli del reso
                    </p>
                  </div>
                  <div className="luxury-card p-5 text-left">
                    <Package className="w-5 h-5 mb-3 text-primary" />
                    <h3 className="font-medium text-sm mb-1">Prepara il Pacco</h3>
                    <p className="text-xs text-muted-foreground">
                      Imballa il prodotto in modo sicuro
                    </p>
                  </div>
                  <div className="luxury-card p-5 text-left">
                    <Truck className="w-5 h-5 mb-3 text-primary" />
                    <h3 className="font-medium text-sm mb-1">Spedisci il Pacco</h3>
                    <p className="text-xs text-muted-foreground">
                      {method === 'free' ? 'Usa l\'etichetta prepagata ricevuta via email' : 'Spedisci all\'indirizzo indicato sopra'}
                    </p>
                  </div>
                </>
              );
            })()}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Link to="/">
              <Button variant="outline" className="luxury-btn-secondary">
                Torna alla Home
              </Button>
            </Link>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default RMAConfirmation;
